import type { ReportMetadata } from '../types';
import { describeError } from './errorMessage';
import { angemeldeterBenutzer } from './angemeldeterBenutzer';
import {
  AblageFehler,
  entferneDatei,
  findeErzeugteUnterlage,
  istDoppelablage,
  istFehlendeKennzeichnung,
  updateDokument,
  uploadDokument,
} from './projektDokumenteService';
import { mitZeitlimit, ZEITLIMIT_LESEN_MS } from './ablageHilfen';
import { findeAusgabeZurFassung, legeAusgabeAn, type AusgabeUnterlage } from './unterlagenAusgabenService';
import { auszugHash, steckbriefAuszugFuerAusgabe } from './projektSteckbrief/abweichungen';
import { ladeSteckbriefQuellen, lesefehlerText } from './projektSteckbrief/quellen';
import { baueSteckbrief } from './projektSteckbrief/steckbrief';
import { heuteLokalIso } from '../components/planungsphase/planungLogic';
import type { DokumentEntwurf, ErzeugendesModul, ProjektDokument } from '../components/dokumente/types';
import { pruefeDatei } from '../components/dokumente/dokumenteLogic';

export type { ErzeugendesModul } from '../components/dokumente/types';

/**
 * DER eine Ablageweg fuer alles, was die Anwendung selbst erzeugt (Plan Stufe
 * 1A.2): Vorankuendigung, SiGe-Plan, A+S-Plan, UfsA, Baustellenordnung/HSE,
 * Handbuch, Einweisung, Planungsphase, Koordinierung. Die Module rufen
 * `legeUnterlageAb` auf und entscheiden selbst nichts ueber Kategorie,
 * Ordner, Kennzeichnung oder Reihenfolge.
 *
 * ═══ WARUM NUR UEBER `uploadDokument` ═════════════════════════════════════
 * Geschrieben wird ausschliesslich ueber `uploadDokument`. Dort ist die
 * Reihenfolge festgenagelt — erst die Datei in den Bucket, dann die Zeile,
 * und scheitert die Zeile, wird die Datei wieder abgeraeumt. Ein zweiter
 * Ablageweg haette dieselbe Reihenfolge nachbauen muessen und waere beim
 * naechsten Policy-Wechsel stehengeblieben (Muster
 * `asplan/ablage/asplanAblage.ts`).
 *
 * ═══ JEDE FASSUNG EINE EIGENE ZEILE, NIE UEBERSCHREIBEN ═══════════════════
 * Die Ablage ueberschreibt grundsaetzlich keine Datei (`upsert: false` auf
 * einen frischen UUID-Pfad, kein update auf dem Bucket). Eine neue Fassung ist
 * eine neue Zeile mit eigenem Stand — Rev. 3.0 muss nach der Freigabe von
 * Rev. 4.0 noch belegbar sein. Einzige Aenderung an einer bestehenden Zeile:
 * die Beschriftung eines Entwurfs, wenn dieselbe Fassung ausgegeben wird
 * (siehe unten).
 *
 * ═══ KEINE DOPPELABLAGE ═══════════════════════════════════════════════════
 * `fassungRef` ist die stabile Kennung der Fassung (Versions-Id,
 * Freigabezeitpunkt). Vor dem Hochladen wird nach ihr gesucht; liegt die
 * Fassung schon, wird NICHTS hochgeladen und die vorhandene Zeile
 * zurueckgegeben (`schon_abgelegt`). Kommen zwei Ablagen derselben Fassung
 * gleichzeitig, faengt der Unique-Index (Nr. 160/161) die zweite mit 23505;
 * `uploadDokument` raeumt ihre Datei wieder ab, und das Ergebnis ist
 * ebenfalls `schon_abgelegt` mit der Zeile des Gewinners.
 *
 * ═══ EINE VERWAISTE DATEI WIRD NIE ALS ERFOLG GEMELDET ═══════════════════
 * Der Rueckbau in `uploadDokument` kann scheitern (Netz weg, Policy weist
 * ab). Das Ergebnis dieses Rueckbaus steht am Fehler (`AblageFehler.rueckbau`),
 * und hier wird er einmal wiederholt. Bleibt die Datei trotzdem liegen, sagt
 * das Ergebnis es: `verwaisteDatei` traegt den Pfad und `fehler` den Satz —
 * auch bei `status: 'schon_abgelegt'`. Sonst belegte die Datei still
 * Speicher, und niemand wuesste je davon.
 *
 * ═══ WIRFT NIE ═══════════════════════════════════════════════════════════
 * Die Ablage haengt an Freigabe und Versand. Ein Fehler hier darf weder die
 * Freigabe ungueltig machen noch das Modul weissbrennen: `legeUnterlageAb`
 * liefert `status: 'fehler'` mit Klartext, das Modul zeigt ihn an und bietet
 * „erneut ablegen" (Plan 1A.3). Der Diskriminator ist eine Zeichenkette, kein
 * boolean — das Projekt uebersetzt ohne `strictNullChecks`.
 *
 * ═══ AUSGABE FUER DEN AENDERUNGSDIENST (Stufe 6.1) ═══════════════════════
 * Gibt der Aufrufer `ausgabe` mit, wird bei einer Freigabe oder einem Versand
 * (nie bei einem Entwurf) der Steckbrief-Auszug der Unterlage eingefroren
 * (`legeAusgabeAn`, Nr. 163) — der Anker, gegen den der Aenderungsdienst
 * spaeter vergleicht. Scheitert das, bleibt die Ablage ein Erfolg; der Grund
 * steht in `hinweise`. Konnte der Steckbrief nicht vollstaendig gelesen
 * werden, wird NICHTS eingefroren: ein Anker aus lauter „ungeklaert" meldete
 * spaeter Abweichungen, die es nie gab.
 *
 * ═══ DER ANKER GEHOERT ZUM AUSGABEVORGANG, NICHT ZUM UPLOAD ══════════════
 * Der Auszug wird VOR dem Hochladen gebildet und kommt im Ergebnis zurueck
 * (`anker`) — auch bei `status: 'fehler'`. „Erneut ablegen" schickt ihn mit
 * (`auftragFuerWiederholung`), und erst dann wird er geschrieben. Wuerde der
 * Auszug beim Wiederholen neu gebildet, froere ein spaeter Klick den
 * INZWISCHEN geaenderten Planungsstand fuer das alte Blatt ein, und echte
 * Abweichungen verschwaenden (Codex-Review 17.10.2026). Dieselbe Regel gilt
 * fuer eine Fassung, die schon frueher als Freigabe oder Versand lag
 * (`schon_abgelegt`) und zu der kein Anker uebernommen wurde: ein heutiger
 * Stand wird NICHT nachgetragen — liegt zur Fassung keine Ausgabe (gleich zu
 * welchem Anlass), sagt der Hinweis, dass sie nicht ueberwacht wird. Lag die
 * Fassung bisher nur als ENTWURF (SiGe-Plan: dieselbe Kennung fuer Entwurf
 * und Versand), ist das die erste Ausgabe — der vorab gebildete Anker wird
 * geschrieben.
 *
 * ═══ FREIGABE OHNE ABLAGE: DER ANKER GEHOERT ZUR FREIGABE ═══════════════
 * Handbuch, Baustellenordnung und A+S-Plan geben frei und legen spaeter ab —
 * Minuten oder Tage spaeter, bei „Erneut ablegen" nach einem Neuladen auch
 * ohne festgehaltenen Auftrag. Dort haelt `halteFreigabeFest` den Anker IM
 * MOMENT DER FREIGABE fest (Anlass `freigabe`, `dokument_id` leer); die
 * Ablage schickt `anker: null` und bildet nie einen. Vorankuendigung, UfsA
 * und SiGe-Plan bleiben beim Vorgangsanker: dort fallen Freigabe/Versand und
 * Ablage in einen Vorgang.
 *
 * ═══ EIN ENTWURF WIRD BEI DER AUSGABE BEFOERDERT ═════════════════════════
 * Lag die Fassung bisher nur als ENTWURF (Schlagwort „Entwurf", ohne
 * „freigegeben"/„versendet") und wird sie jetzt freigegeben oder versendet,
 * beschriftet die Ablage DIESE Zeile um — Titel, Schlagwort, Beschreibung und
 * Stand so, wie eine frische Zeile dieses Anlasses sie bekaeme
 * (`entwurfsBefoerderung`). Sonst stuende die Fassung, die hinausging, als
 * „ENTWURF — nicht freigegeben" in der Ablage. Ein von Hand geaenderter
 * Titel, eine geaenderte Beschreibung oder ein geaenderter Stand der
 * Entwurfszeile werden dabei UEBERSCHRIEBEN — bewusst: danach beschreibt die
 * Zeile die ausgegebene Fassung, nicht mehr den Entwurf. Nur von Hand
 * vergebene Schlagworte bleiben stehen. Danach ist die Zeile keine
 * Entwurfszeile mehr: ein zweiter Versand findet eine ausgegebene Fassung und
 * traegt keinen heutigen Anker nach; ein Doppelklick schreibt dieselben Werte.
 * Scheitert das Umbeschriften, gilt die Ausgabe trotzdem — der Grund steht in
 * `hinweise`, nie still.
 *
 * Die DATEI bleibt, wie sie ist: der Bucket hat keine update-Policy (Nr. 37),
 * ueberschrieben wird nie. Das ist auch nicht noetig: wo das Entwurfs-PDF
 * anders aussieht als die Ausgabe (UfsA: Wasserzeichen „ENTWURF"), steckt das
 * Entwurfskennzeichen in der Kennung, und Entwurf und Ausgabe teilen nie eine
 * Zeile. Beim SiGe-Plan kennt der Generator den Anlass nicht — Entwurf und
 * Versand desselben Standes sind dasselbe Blatt.
 *
 * ═══ NEBENLESEN MIT ZEITLIMIT ═════════════════════════════════════════════
 * Steckbrief und frueherer Anker werden mit `ZEITLIMIT_LESEN_MS` gelesen.
 * Haengt das Netz, laeuft der Upload trotzdem; der Anker fehlt dann, und der
 * Hinweis sagt es. Dasselbe Zeitlimit gilt fuer das Umbeschriften eines
 * Entwurfs.
 */

/** Wann die Fassung abgelegt wird (Entscheid E17). */
export type AblageAnlass = 'freigabe' | 'versand' | 'entwurf';

export interface UnterlageAblegen {
  projectId: string;
  // Ohne 'gbu': die SiFa-GBU legt selbst ab (sifa/gbuService.ts) und traegt
  // kein Modul-Schlagwort — `module: [a.modul]` unten setzt eines voraus.
  modul: Exclude<ErzeugendesModul, 'gbu'>;
  /** PDF oder DOCX. */
  datei: Blob;
  /** Mit Endung. */
  dateiname: string;
  /** Anzeigename, z. B. „Vorankündigung — Fassung vom 17.10.2026". */
  titel: string;
  /** „Rev. 2", „Fassung 3", Datum. */
  stand?: string;
  /** Stabile Kennung der Fassung (Versions-Id, Freigabe-Zeitpunkt); verhindert Doppelablage. */
  fassungRef?: string | null;
  /** Ein Entwurf wird im Titel deutlich als Entwurf markiert. */
  anlass: AblageAnlass;
  beschreibung?: string;
  /**
   * Fuer den Aenderungsdienst: welche Unterlage des Steckbriefs die Fassung
   * ist, und die Projektdaten (`report.metadata`) fuer den Auszug. Fehlt das
   * Feld, wird keine Ausgabe festgehalten (Einweisung, Planungsphase,
   * Koordinierung haben keinen Steckbrief-Auszug).
   */
  ausgabe?: AusgabeAuftrag;
}

/**
 * Der eingefrorene Steckbrief-Auszug einer Ausgabe — gebildet beim
 * URSPRUENGLICHEN Ausgabevorgang, vor dem Hochladen.
 */
export interface AusgabeAnker {
  /** `steckbriefAuszugFuerAusgabe(steckbrief, unterlage)` zum Zeitpunkt der Ausgabe. */
  auszug: Record<string, unknown>;
  /** Der Tag (ISO), an dem der Auszug gebildet wurde. */
  stichtag: string;
}

export interface AusgabeAuftrag {
  unterlage: AusgabeUnterlage;
  metadata: Partial<ReportMetadata> | null;
  /**
   * Weggelassen: der Kern bildet den Auszug vor dem Hochladen und gibt ihn
   * im Ergebnis zurueck (`AblageErgebnis.anker`). Beim Wiederholen uebergibt
   * der Aufrufer genau diesen Anker (`auftragFuerWiederholung`) — so friert
   * auch ein spaeter Versuch den Stand der Ausgabe ein, nicht den von heute.
   * `null` heisst: KEIN Anker aus diesem Vorgang — er wurde bei der Freigabe
   * festgehalten (`halteFreigabeFest`) oder liegt nicht vor. Der Kern bildet
   * dann keinen, prueft nur, ob zur Fassung eine Ausgabe liegt, und meldet
   * sonst, dass die Fassung nicht ueberwacht wird.
   */
  anker?: AusgabeAnker | null;
}

export interface AblageErgebnis {
  status: 'abgelegt' | 'schon_abgelegt' | 'fehler';
  dokument: ProjektDokument | null;
  /**
   * Klartext bei `fehler`. Bei `schon_abgelegt` nur gesetzt, wenn die doppelt
   * hochgeladene Datei nicht wieder entfernt werden konnte (siehe
   * `verwaisteDatei`) — dann ist die Fassung zwar abgelegt, aber etwas
   * gehoert gemeldet.
   */
  fehler: string | null;
  /**
   * Pfad einer Datei, die nach einem Zeilenfehler im Bucket liegen blieb,
   * obwohl sie zweimal entfernt werden sollte. Fehlt oder `null`, wenn
   * nichts verwaist ist.
   */
  verwaisteDatei?: string | null;
  /**
   * Hinweise TROTZ Erfolg — die Ausgabe fuer den Aenderungsdienst konnte
   * nicht festgehalten werden, oder eine bisher als Entwurf abgelegte Zeile
   * liess sich nicht umbeschriften (`ENTWURF_BLEIBT_MARKIERT`). Fehlt, wenn
   * nichts zu melden ist. Nie bei `status: 'fehler'`: dort steht alles in
   * `fehler`.
   */
  hinweise?: string[];
  /**
   * Der Steckbrief-Auszug dieses Ausgabevorgangs — AUCH bei `status:
   * 'fehler'`, damit „Erneut ablegen" ihn mitnimmt (`auftragFuerWiederholung`).
   * Fehlt ohne `ausgabe`, bei einem Entwurf, wenn der Steckbrief nicht
   * lesbar war oder wenn eine schon frueher abgelegte Fassung ohne
   * uebernommenen Anker gefunden wurde.
   */
  anker?: AusgabeAnker;
  /**
   * Die Zeile in `projekt_unterlagen_ausgaben`, die DIESER Ausgabevorgang
   * geschrieben hat: `legeAusgabeAn` neu — oder `schon_vorhanden` bei einer
   * Wiederholung mit dem Anker des ersten Versuchs (dann schrieb der erste
   * Versuch desselben Vorgangs sie, nur die Antwort ging verloren). Fehlt,
   * wenn dieser Vorgang keine geschrieben hat (Entwurf, ohne `ausgabe`,
   * Steckbrief nicht lesbar, Fehler, oder die Fassung lag schon frueher aus —
   * dann steht sie in `vorhandeneAusgabeId`).
   *
   * Wozu: die Datenbank erledigt den passenden Bestandteil einer
   * Paketposition per Trigger auf GENAU dieser Zeile (SQL Nr. 166). Der
   * Abschlussschritt des Moduls (`nachAusgabeBestandteile`) fragt mit ihr nach,
   * was diese Ausgabe erledigt hat, und BUCHT ein dadurch vollstaendiges Paket
   * im Namen dessen, der gerade ausgibt. Eine aeltere Ausgabe darf hier
   * deshalb nie stehen — sonst stuende eine unbeteiligte Handlung als
   * Ausloeser da (Kernregel 7). Rein additiv: das Feld steht nur da, wenn es
   * eine Kennung gibt.
   */
  ausgabeId?: string;
  /**
   * Die Ausgabe, die zu dieser Fassung SCHON VORHER lag (nicht in diesem
   * Vorgang geschrieben): ohne Anker des Vorgangs gefunden
   * (`findeAusgabeZurFassung`) oder von `legeAusgabeAn` als `schon_vorhanden`
   * gemeldet, ohne dass dieser Vorgang ein Wiederholungsversuch war. NUR zur
   * Anzeige — der Abschlussschritt meldet dann, bucht aber nichts.
   */
  vorhandeneAusgabeId?: string;
}

/** Die Kategorie aller von der Anwendung abgelegten Fassungen (Entscheid E16). */
export const EIGENE_UNTERLAGEN_KATEGORIE = 'eigene_unterlagen';

/**
 * Unterordner in der Kategorie `eigene_unterlagen`, je erzeugendem Modul
 * (Plan 1A.5). Ohne Schraegstrich — der ist in der Ablage der Ordnertrenner,
 * und „Baustellenordnung/HSE" wuerde zu zwei Stufen. `asplan` muss dem
 * bisherigen Ordner der A+S-Fassungen entsprechen (`ASPLAN_ORDNER`), damit
 * Alt- und Neubestand im selben Ordner liegen.
 */
export const ORDNER_JE_MODUL: Record<ErzeugendesModul, string> = {
  vorankuendigung: 'Vorankündigung',
  sigeplan: 'SiGe-Plan',
  asplan: 'A+S-Plan',
  unterlage: 'Unterlage für spätere Arbeiten',
  baustellenordnung: 'Baustellenordnung und HSE-Plan',
  handbuch: 'SiGe-Handbuch',
  einweisung: 'Einweisung',
  planungsphase: 'Planungsphase',
  koordinierung: 'Gewerke-Überschneidung',
  // Muss GBU_ABLAGE_ORDNER in sifa/gbuService.ts entsprechen.
  gbu: 'SiFa-Gefährdungsbeurteilungen',
};

/** Das Schlagwort je Anlass — als Chip in der Liste sichtbar. */
const TAG_JE_ANLASS: Record<AblageAnlass, string> = {
  freigabe: 'freigegeben',
  versand: 'versendet',
  entwurf: 'Entwurf',
};

const ENTWURF_PRAEFIX = 'ENTWURF — ';

/**
 * Der Titel in der Ablage. Ein Entwurf bekommt das Praefix in Grossbuchstaben
 * — die Liste sortiert nach Titel und zeigt ihn fett, ein Chip allein geht
 * zwischen fuenf Fassungen unter. Steht „Entwurf" schon vorn, wird nicht
 * doppelt markiert.
 */
export const unterlageTitel = (titel: string, anlass: AblageAnlass): string => {
  const rein = (titel || '').replace(/\s+/g, ' ').trim() || 'Unterlage';
  if (anlass !== 'entwurf') return rein;
  return /^entwurf\b/i.test(rein) ? rein : `${ENTWURF_PRAEFIX}${rein}`;
};

/**
 * Der Ablage-Entwurf einer Fassung — pur, damit die Zuordnung von Kategorie,
 * Ordner, Modul und Kennzeichen ohne Datenbank pruefbar ist.
 *
 * `nurAdmin` bleibt `false`: die Fassung ist das Blatt, nach dem gearbeitet
 * wird; sie hinter das Admin-Schloss zu legen hiesse, sie den Leuten zu
 * entziehen, fuer die sie geschrieben ist.
 */
export const unterlageEntwurf = (a: UnterlageAblegen): DokumentEntwurf => {
  const beschreibung = (a.beschreibung || '').trim();
  return {
    kategorie: EIGENE_UNTERLAGEN_KATEGORIE,
    ordner: ORDNER_JE_MODUL[a.modul],
    titel: unterlageTitel(a.titel, a.anlass),
    beschreibung: a.anlass === 'entwurf'
      ? [beschreibung, 'Entwurfsstand — nicht freigegeben.'].filter(Boolean).join(' ')
      : beschreibung || undefined,
    tags: [TAG_JE_ANLASS[a.anlass]],
    module: [a.modul],
    stand: (a.stand || '').trim() || undefined,
    nurAdmin: false,
    erzeugt_aus: a.modul,
    fassung_ref: (a.fassungRef || '').trim() || null,
  };
};

const fehler = (text: string, verwaist: string | null = null): AblageErgebnis => ({
  status: 'fehler',
  dokument: null,
  fehler: verwaist ? `${text}\n${verwaistText(verwaist)}` : text,
  ...(verwaist ? { verwaisteDatei: verwaist } : {}),
});

/** Der Satz zu einer Datei, die trotz zweier Anlaeufe im Bucket liegen blieb. */
const verwaistText = (pfad: string): string =>
  'Die dabei hochgeladene Datei konnte nicht wieder entfernt werden und belegt weiter Speicher '
  + `(${pfad}). Bitte der Verwaltung melden.`;

/**
 * Hat `uploadDokument` seine Datei nach dem Zeilenfehler nicht wieder
 * weggebracht, wird es hier EINMAL wiederholt — ein Netzaussetzer im ersten
 * Anlauf ist der haeufigste Grund. Zurueck kommt der Pfad, wenn die Datei
 * auch danach als liegengeblieben gelten muss, sonst `null`.
 */
const nochVerwaist = async (e: unknown): Promise<string | null> => {
  const stand = e instanceof AblageFehler ? e.rueckbau : null;
  if (!stand || !stand.verwaist) return null;
  return (await entferneDatei(stand.pfad)) ? null : stand.pfad;
};

/**
 * Die Absage VOR jedem Netzzugriff — oder `null`.
 *
 * 'temp-id' ist die Vorbelegung eines noch nie gespeicherten Projekts
 * (App.tsx, ProjectHub.tsx); eine Ablage darauf liefe in eine Storage-Policy,
 * die kein Projekt findet.
 */
export const pruefeAblageAuftrag = (a: UnterlageAblegen): string | null => {
  if (!a.projectId || a.projectId === 'temp-id') {
    return 'Das Projekt muss erst gespeichert werden, bevor eine Unterlage abgelegt werden kann.';
  }
  if (!(a.modul in ORDNER_JE_MODUL)) return `Unbekanntes Modul „${String(a.modul)}" — die Unterlage wurde nicht abgelegt.`;
  if (!a.datei || typeof a.datei.size !== 'number') return 'Es liegt keine Datei zum Ablegen vor.';
  return pruefeDatei({ name: a.dateiname, size: a.datei.size, type: a.datei.type });
};

const klartext = (e: unknown): string => {
  if (istFehlendeKennzeichnung(e)) {
    return 'Die Ablage kennt die Kennzeichnung erzeugter Unterlagen noch nicht (Migration Nr. 160/161 fehlt). '
      + 'Die Unterlage wurde nicht abgelegt — bitte an die Verwaltung wenden.';
  }
  if (e instanceof AblageFehler && e.code === '23514') {
    return 'Die Ablage kennt die Kategorie „Eigene Unterlagen" noch nicht (Migration Nr. 160/161 fehlt). '
      + 'Die Unterlage wurde nicht abgelegt — bitte an die Verwaltung wenden.';
  }
  return describeError(e);
};

/**
 * Legt eine erzeugte Fassung in der Projektablage ab und haelt — wenn
 * `ausgabe` gesetzt ist — den Steckbrief-Stand fuer den Aenderungsdienst
 * fest. Wirft NIE.
 *
 * Reihenfolge: Pruefung ohne Netz → Anker bilden (nur Freigabe/Versand, nur
 * wenn keiner uebernommen wurde) → Vorab-Blick auf `fassungRef` → Datei und
 * Zeile ueber `uploadDokument` → bei 23505 die Zeile des Gewinners holen →
 * eine vorgefundene Entwurfszeile befoerdern → Ausgabe schreiben (nur nach
 * Erfolg, nur mit Anker des Vorgangs).
 */
export const legeUnterlageAb = async (a: UnterlageAblegen): Promise<AblageErgebnis> => {
  try {
    const absage = pruefeAblageAuftrag(a);
    if (absage) return fehler(absage);
    const vorab = await ankerVorab(a);
    const { ergebnis, warEntwurf } = await befoerdereEntwurf(a, await ablegen(a));
    return mitAusgabe(a, ergebnis, vorab, warEntwurf);
  } catch (e) {
    return fehler(klartext(e));
  }
};

/** Satz vor jedem Hinweis zur Ausgabe — die Ablage selbst ist gelungen. */
export const AUSGABE_NICHT_FESTGEHALTEN =
  'Die Fassung ist abgelegt, aber ihr Steckbrief-Stand für den Änderungsdienst wurde nicht festgehalten:';

/** Der Hinweis, wenn zu einer Fassung kein Anker uebernommen wurde und keiner liegt. */
export const ANKER_NICHT_NACHGETRAGEN =
  `${AUSGABE_NICHT_FESTGEHALTEN} Diese Fassung wird nicht auf Änderungen überwacht `
  + '(Planungsstand bei Freigabe bzw. Versand nicht festgehalten). Ein heutiger Stand wird nicht nachgetragen, '
  + 'weil er spätere Änderungen der Planungsphase verdecken würde.';

/** Was `bildeAusgabeAnker` liefert: den Anker — oder den Grund, warum keiner entstand. */
export interface AnkerBildung {
  anker: AusgabeAnker | null;
  /** Der nackte Grund, wenn `anker` fehlt (Steckbrief nicht lesbar, Zeitlimit). */
  grund: string | null;
  /** `grund` mit dem Vorsatz der Ablage — fuer `hinweise` eines Ablage-Ergebnisses. */
  hinweis: string | null;
}

/**
 * Bildet den Anker JETZT — den Steckbrief-Auszug der Unterlage zum heutigen
 * Stand (Nr. 163). Wirft NIE. Konnte der Steckbrief nicht vollstaendig oder
 * nicht innerhalb des Zeitlimits gelesen werden, gibt es keinen Anker, nur
 * den Grund.
 */
export const bildeAusgabeAnker = async (
  projectId: string,
  ausgabe: Pick<AusgabeAuftrag, 'unterlage' | 'metadata'>,
  zeitlimitMs: number = ZEITLIMIT_LESEN_MS,
): Promise<AnkerBildung> => {
  const ohne = (grund: string): AnkerBildung => ({ anker: null, grund, hinweis: `${AUSGABE_NICHT_FESTGEHALTEN} ${grund}` });
  try {
    const quellen = await mitZeitlimit(ladeSteckbriefQuellen(projectId, ausgabe.metadata), zeitlimitMs);
    const lesefehler = lesefehlerText(quellen.lesefehler);
    if (lesefehler) return ohne(`Der Steckbrief konnte nicht vollständig gelesen werden (${lesefehler}).`);
    const stichtag = heuteLokalIso();
    const steckbrief = baueSteckbrief(quellen, stichtag);
    return { anker: { auszug: steckbriefAuszugFuerAusgabe(steckbrief, ausgabe.unterlage), stichtag }, grund: null, hinweis: null };
  } catch (e) {
    return ohne(`Der Steckbrief konnte nicht gelesen werden (${describeError(e)}).`);
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// FREIGABE FESTHALTEN — der Anker gehoert zur Freigabe (Handbuch, Bauordnung, A+S)
// ═══════════════════════════════════════════════════════════════════════════

export interface FreigabeFesthalten {
  projectId: string;
  unterlage: AusgabeUnterlage;
  /**
   * Die Fassungskennungen der Freigabe — dieselben, unter denen die Blaetter
   * spaeter abgelegt werden (`fassung_ref`). Eine je Blatt: Baustellenordnung
   * und HSE-Plan einer Freigabe sind zwei. Leere werden uebersprungen.
   */
  fassungRefs: ReadonlyArray<string | null | undefined>;
  /**
   * Die Stammdaten des Projekts. OHNE sie wird KEIN Anker gebildet: ein Auszug
   * mit leeren Projekt- und Bauherrendaten meldete spaeter Abweichungen, die
   * es nie gab — derselbe Fall wie ein unlesbarer Steckbrief.
   */
  metadata: Partial<ReportMetadata> | null;
  /**
   * Nur bei „Erneut festhalten": der Anker des ERSTEN Versuchs
   * (`freigabeWiederholung`). Dann wird kein neuer Auszug gebildet — der
   * Planungsstand der Freigabe ist der von damals, nicht der von heute.
   */
  anker?: AusgabeAnker;
}

export interface FreigabeJournal {
  /** `schon_festgehalten`: dieselbe Fassung war schon im Journal (Teil-Unique) — kein Fehler. */
  status: 'festgehalten' | 'schon_festgehalten' | 'fehler';
  /** Klartext bei `fehler`, an der Freigabe zu zeigen. Die Freigabe selbst gilt. */
  hinweis: string | null;
  /**
   * Der Anker dieses Versuchs — gebildet oder uebernommen. Bei `fehler` ist er
   * der Schluessel fuer „Erneut festhalten" (`freigabeWiederholung`); `null`,
   * wenn keiner entstand (Steckbrief unlesbar, keine Stammdaten): dann gibt
   * es nichts zu wiederholen, nur den Hinweis.
   */
  anker: AusgabeAnker | null;
  /**
   * Die Zeilen in `projekt_unterlagen_ausgaben`, die DIESER Vorgang
   * geschrieben hat, je Kennung eine: neu angelegt — oder `schon_vorhanden`
   * bei „Erneut festhalten" mit dem Anker des ersten Versuchs (dann schrieb
   * sie der erste Versuch desselben Vorgangs). Auch bei `fehler`, soweit
   * einzelne Kennungen geschrieben wurden: der Trigger (SQL Nr. 166) hat an
   * ihnen dann schon gearbeitet. Nur sie sind Ausloeser des Abschlussschritts
   * (`nachAusgabeBestandteile` bucht im Namen dessen, der gerade freigibt).
   * Fehlt, wenn dieser Vorgang keine geschrieben hat.
   */
  ausgabeIds?: string[];
  /**
   * Zeilen, die zur Fassung SCHON VORHER lagen (`schon_vorhanden` ohne
   * uebernommenen Anker, auch die eines Wettlaufs) — NUR zur Anzeige, nie
   * Ausloeser (Codex R2c, wie `AblageErgebnis.vorhandeneAusgabeId`).
   */
  vorhandeneAusgabeIds?: string[];
}

/** Vorsatz jedes Hinweises zum Freigabe-Journal — die Freigabe selbst ist gueltig. */
export const FREIGABE_NICHT_FESTGEHALTEN =
  'Die Freigabe gilt, aber ihr Planungsstand für den Änderungsdienst wurde nicht festgehalten:';

/** Grund, wenn die Stammdaten fehlen — kein Anker, kein Wiederholen. */
export const FREIGABE_OHNE_STAMMDATEN =
  'Die Stammdaten des Projekts liegen nicht vor; ohne sie fröre der Planungsstand leere Projekt- und Bauherrendaten ein. '
  + 'Diese Fassung wird nicht auf Änderungen überwacht.';

/**
 * Haelt den Planungsstand EINER Freigabe fest — jetzt, im Moment der
 * Freigabe, unabhaengig davon, ob die Datei-Ablage danach klappt. Wirft NIE.
 *
 * Je Kennung eine Ausgabe (Anlass `freigabe`, `dokument_id` leer); derselbe
 * Auszug fuer alle. Dieselbe Fassung ein zweites Mal (Teil-Unique
 * `project_id, unterlage, fassung_ref, anlass`, auch im Wettlauf 23505)
 * ergibt `schon_festgehalten`, keinen Fehler. Jeder Netzschritt laeuft mit
 * Zeitlimit, damit eine Freigabe nicht an einem haengenden Journal klebt.
 * Der Anker kommt in jedem Fall zurueck: scheitert nur das Schreiben, kann
 * der Aufrufer GENAU diesen Anker noch einmal schicken.
 */
export const halteFreigabeFest = async (
  f: FreigabeFesthalten,
  zeitlimitMs: number = ZEITLIMIT_LESEN_MS,
): Promise<FreigabeJournal> => {
  const fehlerMit = (grund: string, anker: AusgabeAnker | null = null): FreigabeJournal =>
    ({ status: 'fehler', hinweis: `${FREIGABE_NICHT_FESTGEHALTEN} ${grund}`, anker });
  let anker: AusgabeAnker | null = f.anker ?? null;
  try {
    if (!f.projectId || f.projectId === 'temp-id') return fehlerMit('Das Projekt ist noch nicht gespeichert.', anker);
    const refs = Array.from(new Set(f.fassungRefs.map(r => (r || '').trim()).filter(Boolean)));
    if (!refs.length) return fehlerMit('Die Freigabe trägt keine Fassungskennung.', anker);

    if (!anker) {
      if (!f.metadata) return fehlerMit(FREIGABE_OHNE_STAMMDATEN);
      const bildung = await bildeAusgabeAnker(f.projectId, { unterlage: f.unterlage, metadata: f.metadata }, zeitlimitMs);
      if (!bildung.anker) return fehlerMit(bildung.grund || 'Der Steckbrief konnte nicht gelesen werden.');
      anker = bildung.anker;
    }

    let schon = 0;
    const fehler: string[] = [];
    const ids: string[] = [];
    const vorhandene: string[] = [];
    // Eine Wiederholung DESSELBEN Vorgangs bringt den Anker des ersten
    // Versuchs mit — eine schon liegende Zeile ist dann die eigene, aber nur,
    // wenn sie von derselben Person und mit demselben Anker stammt
    // (`istEigeneAusgabe`, Codex R3).
    const wiederholung = f.anker !== undefined;
    const ankerHash = auszugHash(anker.auszug);
    let nutzerId: string | null | undefined;
    const ich = async (): Promise<string | null> => {
      if (nutzerId === undefined) nutzerId = (await angemeldeterBenutzer()).id ?? null;
      return nutzerId;
    };
    for (const ref of refs) {
      const ergebnis = await mitZeitlimit(legeAusgabeAn({
        projectId: f.projectId,
        unterlage: f.unterlage,
        fassungRef: ref,
        anlass: 'freigabe',
        auszug: anker.auszug,
        dokumentId: null,
      }), zeitlimitMs);
      if (ergebnis.status === 'fehler') fehler.push(ergebnis.fehler || 'Unbekannter Fehler.');
      else if (ergebnis.status === 'schon_vorhanden') schon += 1;
      const id = ausgabeIdVon(ergebnis.status === 'fehler' ? null : ergebnis.ausgabe);
      const eigene = ergebnis.status === 'ok'
        || (ergebnis.status === 'schon_vorhanden' && wiederholung && istEigeneAusgabe({
          status: 'schon_vorhanden', wiederholung, ausgabe: ergebnis.ausgabe, nutzerId: await ich(), ankerHash,
        }));
      const ziel = eigene ? ids : vorhandene;
      if (id && !ziel.includes(id)) ziel.push(id);
    }
    const mitIds = (j: FreigabeJournal): FreigabeJournal => ({
      ...j,
      ...(ids.length ? { ausgabeIds: ids } : {}),
      ...(vorhandene.length ? { vorhandeneAusgabeIds: vorhandene } : {}),
    });
    if (fehler.length) return mitIds(fehlerMit(Array.from(new Set(fehler)).join(' '), anker));
    return mitIds({ status: schon === refs.length ? 'schon_festgehalten' : 'festgehalten', hinweis: null, anker });
  } catch (e) {
    return fehlerMit(describeError(e), anker);
  }
};

/**
 * Ist eine Ausgabe-Zeile die EIGENE dieses Vorgangs? (Codex R3)
 *  - neu geschrieben (`ok`): ja;
 *  - schon vorhanden: nur bei einer Wiederholung desselben Vorgangs, UND nur,
 *    wenn die Zeile von derselben Person stammt (`erstellt_von` =
 *    angemeldeter Nutzer) UND denselben Anker traegt (`auszug_hash`). Eine
 *    andere Sitzung, die den Wettlauf gewann, oder ein frueherer Stand sind
 *    nie Ausloeser — sonst buchte der Abschlussschritt ein Paket mit falschem
 *    `bookedBy`.
 * Rein.
 */
export const istEigeneAusgabe = (a: {
  status: 'ok' | 'schon_vorhanden';
  wiederholung: boolean;
  ausgabe: { erstellt_von?: string | null; auszug_hash?: string | null } | null | undefined;
  nutzerId: string | null | undefined;
  ankerHash: string;
}): boolean => {
  if (a.status === 'ok') return true;
  if (!a.wiederholung) return false;
  const von = (a.ausgabe?.erstellt_von || '').trim().toLowerCase();
  const ich = (a.nutzerId || '').trim().toLowerCase();
  if (!von || !ich || von !== ich) return false;
  const hash = (a.ausgabe?.auszug_hash || '').trim();
  return !!hash && hash === a.ankerHash;
};

/** Die Kennung einer Ausgabe-Zeile — leer (Antwort unlesbar) zaehlt als keine. */
const ausgabeIdVon = (a: { id?: string | null } | null | undefined): string | null => {
  const id = (a?.id || '').trim();
  return id || null;
};

/**
 * Der Auftrag fuer „Erneut festhalten" — oder `null`, wenn es nichts zu
 * wiederholen gibt: das Journal ist geschrieben, oder es entstand kein Anker
 * (dann hiesse Wiederholen einen HEUTIGEN Stand bilden). Mit Anker geht genau
 * dieser noch einmal; der Steckbrief wird nicht neu gelesen.
 */
export const freigabeWiederholung = (f: FreigabeFesthalten, journal: FreigabeJournal): FreigabeFesthalten | null =>
  (journal.status === 'fehler' && journal.anker ? { ...f, anker: journal.anker } : null);

/**
 * Der Auftrag fuer „Erneut ablegen": derselbe Auftrag, aber mit dem Anker des
 * ersten Versuchs. Hatte der keinen (Steckbrief nicht lesbar, Kern warf),
 * steht `anker: null` — der Kern traegt dann KEINEN heutigen Stand nach.
 * Ein schon uebernommener Anker bleibt (Wiederholung einer Wiederholung).
 * Ohne `ausgabe` oder fuer einen Entwurf kommt der Auftrag unveraendert zurueck.
 */
export const auftragFuerWiederholung = <A extends UnterlageAblegen>(
  a: A,
  ergebnis: Pick<AblageErgebnis, 'anker'> | null | undefined,
): A => {
  if (!a.ausgabe || a.anlass === 'entwurf') return a;
  if (a.ausgabe.anker !== undefined) return a;
  return { ...a, ausgabe: { ...a.ausgabe, anker: ergebnis?.anker ?? null } };
};

/** Nur fuer Freigabe und Versand ohne uebernommenen Anker wird einer gebildet. */
const ankerVorab = async (a: UnterlageAblegen): Promise<AnkerBildung | null> => {
  if (!a.ausgabe || a.anlass === 'entwurf' || a.ausgabe.anker !== undefined) return null;
  return bildeAusgabeAnker(a.projectId, a.ausgabe);
};

/**
 * Schreibt die Ausgabe mit dem Anker des Vorgangs (Nr. 163). Liefert den
 * Hinweis, wenn das nicht gelang, sonst `null` — und die Kennung der Zeile
 * (neu oder schon vorhanden). Wirft NIE. `legeAusgabeAn` erkennt eine schon
 * vorhandene Ausgabe ueber `fassungRef` + `anlass` — ein Wiederholen
 * verdoppelt nichts.
 */
const ausgabeHinweis = async (
  a: UnterlageAblegen,
  anlass: 'freigabe' | 'versand',
  ausgabe: AusgabeAuftrag,
  dokument: ProjektDokument,
  anker: AusgabeAnker,
  wiederholung: boolean,
): Promise<{ hinweis: string | null; ausgabeId: string | null; eigene: boolean }> => {
  try {
    const ergebnis = await legeAusgabeAn({
      projectId: a.projectId,
      unterlage: ausgabe.unterlage,
      fassungRef: (a.fassungRef || '').trim() || null,
      anlass,
      auszug: anker.auszug,
      dokumentId: (dokument.id || '').trim() || null,
    });
    if (ergebnis.status === 'fehler') {
      return { hinweis: `${AUSGABE_NICHT_FESTGEHALTEN} ${ergebnis.fehler || 'Unbekannter Fehler.'}`, ausgabeId: null, eigene: false };
    }
    // `schon_vorhanden` zaehlt nur nach Pruefung von Person und Anker als
    // eigene (istEigeneAusgabe) — die Person wird nur dann gelesen.
    const eigene = ergebnis.status === 'ok' || (wiederholung && istEigeneAusgabe({
      status: 'schon_vorhanden',
      wiederholung,
      ausgabe: ergebnis.ausgabe,
      nutzerId: (await angemeldeterBenutzer()).id,
      ankerHash: auszugHash(anker.auszug),
    }));
    return { hinweis: null, ausgabeId: ausgabeIdVon(ergebnis.ausgabe), eigene };
  } catch (e) {
    return { hinweis: `${AUSGABE_NICHT_FESTGEHALTEN} ${describeError(e)}`, ausgabeId: null, eigene: false };
  }
};

/**
 * Kein Anker des Vorgangs: NICHTS nachtragen. Liegt zur Fassung schon eine
 * Ausgabe — gleich zu welchem Anlass: die der Freigabe, eines frueheren
 * Versuchs oder des Freigabe-Journals (`halteFreigabeFest`) —, gibt es
 * nichts zu melden (Hinweis leer, Kennung der vorhandenen Zeile); sonst sagt
 * der Hinweis, dass die Fassung nicht ueberwacht wird. Kann das nicht
 * geprueft werden, steht der Grund im Hinweis — nie Stille.
 */
const ohneAnkerHinweis = async (
  a: UnterlageAblegen,
  ausgabe: AusgabeAuftrag,
): Promise<{ hinweis: string; ausgabeId: string | null }> => {
  const ref = (a.fassungRef || '').trim() || null;
  if (!ref) return { hinweis: ANKER_NICHT_NACHGETRAGEN, ausgabeId: null };
  try {
    const vorhanden = await mitZeitlimit(findeAusgabeZurFassung(a.projectId, ausgabe.unterlage, ref));
    return vorhanden
      ? { hinweis: '', ausgabeId: ausgabeIdVon(vorhanden) }
      : { hinweis: ANKER_NICHT_NACHGETRAGEN, ausgabeId: null };
  } catch (e) {
    return {
      hinweis: `${AUSGABE_NICHT_FESTGEHALTEN} Ob ein früherer Stand vorliegt, konnte nicht geprüft werden (${describeError(e)}); nachgetragen wurde nichts.`,
      ausgabeId: null,
    };
  }
};

/**
 * Haengt die Kennung der Ausgabe an — nur, wenn es eine gibt (rein additiv):
 * `eigene` als `ausgabeId` (dieser Vorgang hat sie geschrieben), eine aeltere
 * als `vorhandeneAusgabeId` (nur Anzeige, siehe AblageErgebnis).
 */
const mitAusgabeKennung = (
  ergebnis: AblageErgebnis,
  k: { eigene?: string | null; vorhanden?: string | null },
): AblageErgebnis => ({
  ...ergebnis,
  ...(k.eigene ? { ausgabeId: k.eigene } : {}),
  ...(k.vorhanden ? { vorhandeneAusgabeId: k.vorhanden } : {}),
});

/**
 * Lag die Fassung bisher NUR als Entwurf in der Ablage? Dann ist die
 * Freigabe oder der Versand derselben Bytes die erste Ausgabe — mit eigenem
 * Anker (SiGe-Plan: dieselbe Kennung fuer Entwurf und Versand).
 */
const istEntwurfsZeile = (d: Pick<ProjektDokument, 'tags'>): boolean => {
  const tags = new Set((d.tags || []).map(t => (t || '').trim().toLowerCase()));
  return tags.has(TAG_JE_ANLASS.entwurf.toLowerCase())
    && !tags.has(TAG_JE_ANLASS.freigabe.toLowerCase())
    && !tags.has(TAG_JE_ANLASS.versand.toLowerCase());
};

const mitHinweis = (ergebnis: AblageErgebnis, hinweis: string | null): AblageErgebnis =>
  (hinweis ? { ...ergebnis, hinweise: [...(ergebnis.hinweise || []), hinweis] } : ergebnis);

/** Was sich an einer Entwurfszeile aendert, wenn dieselbe Fassung ausgegeben wird. */
export interface EntwurfsBefoerderung {
  titel: string;
  /** `null` statt weglassen: ein fehlender Schluessel loescht in PostgREST nichts. */
  beschreibung: string | null;
  tags: string[];
  /** Mit dem Titel, damit „Stand …" im Titel und die Spalte nicht auseinanderlaufen. */
  stand: string | null;
}

/**
 * Der Zielzustand einer Entwurfszeile, wenn dieselbe Fassung freigegeben oder
 * versendet wird — oder `null`, wenn es nichts zu befoerdern gibt (Auftrag
 * ist selbst ein Entwurf, Zeile ist keine Entwurfszeile mehr).
 *
 * Titel, Beschreibung und Stand genau wie bei einer frischen Zeile dieses
 * Anlasses (`unterlageEntwurf`): ohne „ENTWURF — ", ohne den Entwurfssatz,
 * und mit der Beschreibung des Moduls fuer die Ausgabe (die des Entwurfs
 * sagte etwa „kein Export, keine Leistungsbuchung"). Das Schlagwort „Entwurf"
 * wird durch das des Anlasses ersetzt; von Hand vergebene Schlagworte
 * bleiben. Kategorie, Ordner und Module sind bei beiden Anlaessen gleich.
 */
export const entwurfsBefoerderung = (
  zeile: Pick<ProjektDokument, 'tags'>,
  a: UnterlageAblegen,
): EntwurfsBefoerderung | null => {
  if (a.anlass === 'entwurf' || !istEntwurfsZeile(zeile)) return null;
  const frisch = unterlageEntwurf(a);
  const entwurf = TAG_JE_ANLASS.entwurf.toLowerCase();
  const gesehen = new Set<string>();
  const tags: string[] = [];
  for (const roh of zeile.tags || []) {
    const rein = (roh || '').trim();
    const tag = rein.toLowerCase() === entwurf ? TAG_JE_ANLASS[a.anlass] : rein;
    if (!tag || gesehen.has(tag.toLowerCase())) continue;
    gesehen.add(tag.toLowerCase());
    tags.push(tag);
  }
  return { titel: frisch.titel, beschreibung: frisch.beschreibung ?? null, tags, stand: frisch.stand ?? null };
};

/** Vorsatz des Hinweises, wenn eine Entwurfszeile nicht umbeschriftet werden konnte. */
export const ENTWURF_BLEIBT_MARKIERT =
  'Die Fassung ist abgelegt, steht in „Dokumente" aber noch als ENTWURF:';

const entwurfBleibtHinweis = (anlass: AblageAnlass, grund: string): string =>
  `${ENTWURF_BLEIBT_MARKIERT} die Umbeschriftung auf „${TAG_JE_ANLASS[anlass]}" ist nicht bestätigt (${grund}). `
  + 'Bitte dort Titel, Schlagwort und Beschreibung von Hand anpassen.';

/**
 * Befoerdert eine vorgefundene Entwurfszeile (siehe Dateikopf). Wirft NIE.
 * `warEntwurf` sagt `mitAusgabe`, dass dies die erste Ausgabe der Fassung
 * ist — festgestellt VOR dem Umbeschriften, denn danach sieht die Zeile aus
 * wie eine schon ausgegebene. Zurueck kommt die umbeschriftete Zeile, oder
 * bei einem Fehler die alte mit Hinweis; die Ausgabe gilt in jedem Fall.
 */
const befoerdereEntwurf = async (
  a: UnterlageAblegen,
  ergebnis: AblageErgebnis,
): Promise<{ ergebnis: AblageErgebnis; warEntwurf: boolean }> => {
  if (ergebnis.status !== 'schon_abgelegt' || !ergebnis.dokument) return { ergebnis, warEntwurf: false };
  const ziel = entwurfsBefoerderung(ergebnis.dokument, a);
  if (!ziel) return { ergebnis, warEntwurf: false };
  try {
    const dokument = await mitZeitlimit(updateDokument(ergebnis.dokument.id, ziel));
    return { ergebnis: { ...ergebnis, dokument }, warEntwurf: true };
  } catch (e) {
    return { ergebnis: mitHinweis(ergebnis, entwurfBleibtHinweis(a.anlass, describeError(e))), warEntwurf: true };
  }
};

const mitAusgabe = async (
  a: UnterlageAblegen,
  ergebnis: AblageErgebnis,
  vorab: AnkerBildung | null,
  warEntwurf: boolean,
): Promise<AblageErgebnis> => {
  const anlass = a.anlass;
  const ausgabe = a.ausgabe;
  if (!ausgabe || anlass === 'entwurf') return ergebnis;

  // Uebernommen: der Aufrufer bringt den Anker des ersten Versuchs mit — oder
  // `null`: keiner aus diesem Vorgang (Freigabe-Journal oder nie gebildet).
  const uebernommen = ausgabe.anker !== undefined;
  const anker: AusgabeAnker | null = uebernommen ? (ausgabe.anker ?? null) : (vorab?.anker ?? null);

  if (ergebnis.status === 'fehler' || !ergebnis.dokument) {
    // Der Anker gehoert zum Vorgang, nicht zum Upload: zurueck fuer „Erneut ablegen".
    return anker ? { ...ergebnis, anker } : ergebnis;
  }

  // Erste Ausgabe dieser Fassung: frisch abgelegt — oder sie lag bisher nur
  // als Entwurf (festgestellt vor dem Umbeschriften, `befoerdereEntwurf`).
  // Dann gilt der Anker DIESES Vorgangs.
  const ersteAusgabe = ergebnis.status === 'abgelegt' || warEntwurf;

  // Erste Ausgabe, aber eben kein Anker gebildet (Steckbrief nicht lesbar,
  // Zeitlimit): der Grund steht am Ergebnis, nachgetragen wird nichts.
  if (!uebernommen && !anker && ersteAusgabe) return mitHinweis(ergebnis, vorab?.hinweis ?? null);

  // Kein Anker des Vorgangs — oder eine schon frueher ausgegebene Fassung
  // ohne uebernommenen Anker: der heute gebildete Auszug ist nicht der Stand,
  // mit dem sie hinausging. Nichts nachtragen; melden, wenn keiner liegt.
  if (!anker || (!uebernommen && !ersteAusgabe)) {
    // Keine Ausgabe DIESES Vorgangs — eine gefundene ist aelter: nur Anzeige.
    const ohne = await ohneAnkerHinweis(a, ausgabe);
    return mitAusgabeKennung(mitHinweis(ergebnis, ohne.hinweis), { vorhanden: ohne.ausgabeId });
  }

  const geschrieben = await ausgabeHinweis(a, anlass, ausgabe, ergebnis.dokument, anker, uebernommen);
  // `schon_vorhanden` gehoert nur dann zu diesem Vorgang, wenn er die
  // Wiederholung eines Versuchs ist (uebernommener Anker) UND die Zeile von
  // derselben Person mit demselben Anker stammt (istEigeneAusgabe). Sonst lag
  // sie schon frueher oder kam aus einer anderen Sitzung — nur Anzeige.
  const eigene = geschrieben.ausgabeId && geschrieben.eigene ? geschrieben.ausgabeId : null;
  return mitAusgabeKennung(
    { ...mitHinweis(ergebnis, geschrieben.hinweis), anker },
    { eigene, vorhanden: eigene ? null : geschrieben.ausgabeId },
  );
};

const ablegen = async (a: UnterlageAblegen): Promise<AblageErgebnis> => {
  let ref: string | null = null;
  try {
    ref = (a.fassungRef || '').trim() || null;
    if (ref) {
      const vorhanden = await findeErzeugteUnterlage(a.projectId, a.modul, ref);
      if (vorhanden) return { status: 'schon_abgelegt', dokument: vorhanden, fehler: null };
    }

    const wer = await angemeldeterBenutzer();
    const dokument = await uploadDokument({
      projectId: a.projectId,
      datei: a.datei,
      dateiname: a.dateiname,
      entwurf: unterlageEntwurf(a),
      benutzerId: wer.id,
      benutzerName: wer.name,
    });
    return { status: 'abgelegt', dokument, fehler: null };
  } catch (e) {
    // Zuerst die eigene Datei: liegt sie nach dem Zeilenfehler noch da, ist
    // das unabhaengig davon zu melden, wie der Fehler sonst ausgeht.
    const verwaist = await nochVerwaist(e);
    if (istDoppelablage(e) && ref) {
      // Wettlauf: eine zweite Ablage derselben Fassung war schneller. Die
      // Zeile des Gewinners ist das richtige Ergebnis — mit Hinweis, falls
      // die eigene Datei nicht wieder weggegangen ist.
      try {
        const gewinner = await findeErzeugteUnterlage(a.projectId, a.modul, ref);
        if (gewinner) {
          return {
            status: 'schon_abgelegt',
            dokument: gewinner,
            fehler: verwaist ? verwaistText(verwaist) : null,
            ...(verwaist ? { verwaisteDatei: verwaist } : {}),
          };
        }
      } catch {
        // Dann unten der ehrliche Satz: abgelegt ist sie, nur nicht auffindbar.
      }
      return fehler(
        'Diese Fassung liegt bereits in der Ablage, ließ sich aber gerade nicht wiederfinden. '
        + 'Bitte die Dokumentenablage neu laden.',
        verwaist,
      );
    }
    return fehler(klartext(e), verwaist);
  }
};
